import globToRegExp from 'glob-to-regexp'
import type { LogicDefinitionItem as LogicCondition, Widget } from '@dashboard/types/config'
import { describe, pageLabel, pageRules } from '@shared/targeting/pageConditions'

export type PageMatch = { matches: boolean; reason: string }

export const TURNED_OFF_REASON = "Its page rules match, but it's turned off"

/** Accepts a path or a full URL: drops protocol, host, query and hash, adds the leading slash and drops a trailing one. */
export function normalizePagePath(input: string): string | null {
  const value = input
    .trim()
    .replace(/^(?:[a-z][a-z\d+.-]*:)?\/\/[^/?#]*/i, '')
    .replace(/[?#].*$/, '')
  if (!value.trim()) {
    return input.trim() ? '/' : null
  }
  return `/${value.replace(/^\/+/, '')}`.replace(/(.)\/+$/, '$1')
}

// Same semantics as the snippet's current-page `matches pattern`: one leading/trailing slash ignored, `*` wildcard.
function pathMatches(pattern: string, path: string): boolean {
  const strip = (value: string) => value.replace(/^\//, '').replace(/\/$/, '')
  return globToRegExp(strip(pattern)).test(strip(path))
}

type Evaluation = { match: boolean; undecided: LogicCondition[] }

/** Evaluates a condition tree against a page path; conditions a path can't decide are assumed to pass and returned. */
function evaluate(condition: LogicCondition, path: string): Evaluation {
  if (condition.not) {
    const inner = evaluate(condition.not, path)
    return inner.undecided.length ? { match: true, undecided: [condition] } : { match: !inner.match, undecided: [] }
  }
  if (condition.and) {
    const results = condition.and.map((child) => evaluate(child, path))
    return results.some((result) => !result.match)
      ? { match: false, undecided: [] }
      : { match: true, undecided: results.flatMap((result) => result.undecided) }
  }
  if (condition.or) {
    const results = condition.or.map((child) => evaluate(child, path))
    const decided = results.find((result) => result.match && !result.undecided.length)
    return decided ?? results.find((result) => result.match) ?? { match: false, undecided: [] }
  }
  // The page filter has no hostname, so domain rules stay undecided like every other non-path condition.
  if (
    condition.$source === 'pageviews' &&
    condition.operator === 'matches pattern' &&
    condition.$type === 'path' &&
    condition.occurrence === 'last'
  ) {
    return { match: typeof condition.value === 'string' && pathMatches(condition.value, path), undecided: [] }
  }
  return { match: true, undecided: [condition] }
}

type Shape = { allow: string[] | null; block: string[] | null }

const rulePaths = (condition: LogicCondition): string[] | null =>
  pageRules(condition)?.map((rule) => rule.path ?? '*') ?? null

/** Recognizes "only on …", "everywhere except …" and their combination; anything else has no shape. */
function shapeOf(condition: LogicCondition): Shape | null {
  const allow = rulePaths(condition)
  if (allow) {
    return { allow, block: null }
  }
  const block = condition.not ? rulePaths(condition.not) : null
  if (block) {
    return { allow: null, block }
  }
  if (condition.and?.length === 2) {
    const [first, second] = condition.and
    for (const [allowing, blocking] of [
      [first, second],
      [second, first]
    ]) {
      const allowPaths = allowing ? rulePaths(allowing) : null
      const blockPaths = blocking?.not ? rulePaths(blocking.not) : null
      if (allowPaths && blockPaths) {
        return { allow: allowPaths, block: blockPaths }
      }
    }
  }
  return null
}

/** Separates the page-only part of a top-level AND from the other conditions it is combined with. */
function pagePart(condition: LogicCondition): LogicCondition | null {
  if (shapeOf(condition)) {
    return condition
  }
  const page = condition.and?.filter((child) => shapeOf(child))
  if (!page?.length) {
    return null
  }
  return page.length === 1 ? (page[0] ?? null) : { and: page }
}

const label = (path: string) => pageLabel({ domain: null, path })
const listed = (paths: string[], joiner: string) =>
  paths.length === 1 ? (paths[0] ?? '') : `${paths.slice(0, -1).join(', ')} ${joiner} ${paths.at(-1)}`

function matchedReason(shape: Shape | null, path: string): string {
  const allowed = shape?.allow?.find((pattern) => pathMatches(pattern, path))
  if (allowed !== undefined && !/^\/?\*$/.test(allowed)) {
    return allowed.endsWith('/*') ? `Path starts with ${label(allowed.slice(0, -2))}` : `Path is ${label(allowed)}`
  }
  if (shape && !shape.allow && shape.block) {
    return `All pages except ${listed(shape.block.map(label), 'and')}`
  }
  return shape || allowed !== undefined ? 'All pages' : 'Matches its page rules'
}

function unmatchedReason(condition: LogicCondition, shape: Shape | null, path: string): string {
  const blocked = shape?.block?.find((pattern) => pathMatches(pattern, path))
  if (blocked !== undefined) {
    return `Hidden on ${label(blocked)}`
  }
  if (shape?.allow && !shape.allow.some((pattern) => pathMatches(pattern, path))) {
    if (!shape.allow.length) {
      return 'Shown on no pages'
    }
    const [only] = shape.allow
    if (shape.allow.length === 1 && only?.endsWith('/*')) {
      return `Only on paths starting with ${label(only.slice(0, -2))}`
    }
    return `Only on ${listed(shape.allow.map(label), 'or')}`
  }
  return condition.or?.length === 0 ? 'Shown on no pages' : "Its page rules don't match this page"
}

/** Whether a widget's page rules show it on `path`, and why. Embedded widgets match where their snippet was found. */
export function matchWidgetPage(
  widget: Pick<Widget, 'type' | 'visibility' | 'isEnabled'>,
  path: string,
  embedPaths: readonly string[] | undefined
): PageMatch {
  let result: PageMatch
  if (widget.type === 'inline') {
    // Undefined means embed pages could not be loaded; don't report a setup problem for a reporting gap.
    const found = embedPaths?.includes(path)
    result =
      found === undefined
        ? { matches: true, reason: 'Embed pages unavailable' }
        : { matches: found, reason: found ? 'Embed found on this page' : 'Embed not found on this page' }
  } else {
    const conditions = widget.visibility?.conditions
    if (!conditions) {
      result = { matches: true, reason: 'All pages' }
    } else {
      const page = pagePart(conditions)
      const shape = page ? shapeOf(page) : null
      const { match, undecided } = evaluate(conditions, path)
      result = match
        ? {
            matches: true,
            reason: `${page ? matchedReason(shape, path) : 'All pages'}${
              undecided.length ? ` · also needs ${undecided.map(describe).join(' and ')}` : ''
            }`
          }
        : { matches: false, reason: unmatchedReason(page ?? conditions, shape, path) }
    }
  }
  return result.matches && !widget.isEnabled ? { matches: false, reason: TURNED_OFF_REASON } : result
}

/** "Embed on 3 pages" with up to two of the pages named. */
export function summarizeEmbedPages(paths: readonly string[]): { summary: string; detail: string | null } {
  const [first, second] = paths
  return {
    summary: `Embed on ${paths.length} ${paths.length === 1 ? 'page' : 'pages'}`,
    detail: !first
      ? null
      : paths.length <= 2
        ? listed(paths.slice(0, 2), 'and')
        : `${first}, ${second} and ${paths.length - 2} more`
  }
}
